/**
 * A submission's derived signals — rating and sentiment — as symbols.
 *
 * One component so the grids, the detail page and any future report all read the
 * same. Stars carry the scale with them: a filled-of-total shape says "4 out of
 * 5" at a glance, where a bare "4" is meaningless without knowing the scale, and
 * 4/5 and 4/10 are different answers.
 */
import type { FeedbackSubmission } from '../../types/feedback-submission.types';
import { SENTIMENT_ICON } from './feedback-display';

/** Read-only stars. `scale` is required — a rating without one cannot be drawn. */
export const Stars = ({ value, scale, size = 'sm' }: {
  value: number; scale: number; size?: 'sm' | 'md';
}) => (
  <span className={`whitespace-nowrap ${size === 'md' ? 'text-lg' : 'text-sm'}`}
    title={`${value} of ${scale}`} aria-label={`${value} out of ${scale}`}>
    {Array.from({ length: scale }, (_, i) => (
      <i key={i}
        className={`pi ${i < value ? 'pi-star-fill text-amber-400' : 'pi-star text-gray-300 dark:text-gray-600'}`}
        style={{ fontSize: 'inherit' }} />
    ))}
  </span>
);

/** Thumb up / bar / thumb down, coloured by meaning. Falls back to the code. */
export const SentimentMark = ({ value, size = 'sm' }: { value: string; size?: 'sm' | 'md' }) => {
  const code = (value || '').toUpperCase();
  const icon = SENTIMENT_ICON[code];
  const tone = code === 'POSITIVE' ? 'text-emerald-600 dark:text-emerald-400'
    : code === 'NEGATIVE' ? 'text-rose-600 dark:text-rose-400'
      : 'text-ink-muted';
  if (!icon) {
    return <span className={`text-xs font-semibold ${tone}`}>{value}</span>;
  }
  return (
    <i className={`pi ${icon} ${tone} ${size === 'md' ? 'text-xl' : 'text-base'}`}
      title={code} aria-label={code} />
  );
};

/** The grid cell: whichever signals this submission actually carries. */
const SignalCell = ({ d }: { d: FeedbackSubmission }) => {
  const rating = d.derivedSignals?.rating;
  const scale = d.derivedSignals?.ratingScale ?? 5;
  const sentiment = d.derivedSignals?.sentiment;

  if ((rating === null || rating === undefined) && !sentiment) {
    return <span className="text-xs text-gray-400">—</span>;
  }
  return (
    <div className="flex flex-col items-center gap-1">
      {rating !== null && rating !== undefined && <Stars value={rating} scale={scale} />}
      {sentiment && <SentimentMark value={sentiment} />}
    </div>
  );
};

export default SignalCell;
